'use client';

import * as React from 'react';

import { cn } from '@repo/ui/lib/utils';
import { Tabs, TabsList, TabsTrigger } from './tabs';

/** Default date-range options shared by Portfolio, Vaults, Risk & Liquidity, and Activity. */
const DEFAULT_PERIOD_OPTIONS = ['1W', '1M', '3M', '6M', 'YTD', '1Y'] as const;

interface PeriodSelectorProps {
  value: string;
  onChange: (value: string) => void;
  options?: readonly string[];
  className?: string;
  disabled?: boolean;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

/**
 * Segmented date-range control (1W/1M/3M/6M/YTD/1Y) — a pill-styled
 * `Tabs` (`variant="segmented"`) rather than a net-new component, since the
 * behavior (single-choice, keyboard-navigable) is exactly Radix Tabs.
 */
function PeriodSelector({
  value,
  onChange,
  options = DEFAULT_PERIOD_OPTIONS,
  className,
  disabled = false,
  'aria-label': ariaLabel = 'Select time period',
  'aria-describedby': ariaDescribedBy
}: PeriodSelectorProps) {
  if (disabled) {
    return (
      <div className={cn('flex flex-col gap-2', className)}>
        <div
          role="group"
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-disabled="true"
          className="bg-rowa-surface-control border-rowa-border-glass-highlight shadow-rowa-control rounded-rowa-full backdrop-blur-rowa-control inline-flex items-center gap-0.5 border p-1"
        >
          {options.map((option) => (
            <button
              key={option}
              type="button"
              disabled
              aria-pressed={option === value}
              className={cn(
                'text-rowa-label-md rounded-rowa-full px-3 py-1.5 whitespace-nowrap opacity-50',
                option === value
                  ? 'bg-rowa-surface-dock-active border-rowa-status-success-border text-rowa-text-link shadow-rowa-control border'
                  : 'text-rowa-text-secondary'
              )}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <Tabs value={value} onValueChange={onChange} className={className}>
      <TabsList variant="segmented" aria-label={ariaLabel} aria-describedby={ariaDescribedBy}>
        {options.map((option) => (
          <TabsTrigger key={option} value={option}>
            {option}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

export { PeriodSelector, DEFAULT_PERIOD_OPTIONS };
export type { PeriodSelectorProps };
